import React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ui, state as STATE } from '../../../../../../interfaces/interfaces';
import useDebugInformation from '../../../../../../hooks/useDebugInformation';
import { reducer, initialStageState, ACTIONS } from './helpers';
import { getActiveStep } from '../../../../../../utilities/validation';
import { useAppSelector } from "@store/hooks";

const name = "Stage1Panel"

type Props = {
    parent: string
}

const Stage1Panel: React.FC<Props> = ({ parent }) => {
    const [state, dispatch] = React.useReducer(reducer, initialStageState);
    const jobsSearchComplete = useAppSelector(({ state }) => state.ProblemComboJobValid)
    const addressSearchComplete = useAppSelector(({ ui }) => ui.ProblemAddressString)
    const coverageSearchComplete = useAppSelector(({ state }) => state.ProblemCoverageValid)
    const requiredOptionsComplete = useAppSelector(({ state }) => state.RequiredOptionsValid)
    const restrictionExpiresIn30Days = useAppSelector(({ state }) => state.RestrictionExpiresIn30Days)
    const stage1Complete = useAppSelector(({ ui }) => ui.Stage1Complete)
    const steps = React.useMemo(() => Object.values(state), [state])
    const [activeStep, setActiveStep] = React.useState(getActiveStep(steps))

    React.useEffect(() => {
        STATE.Step1_1Valid = jobsSearchComplete
        dispatch(ACTIONS.SET_JOB_TYPE_STATE(jobsSearchComplete)) //JOB SEARCH STATE
    }, [jobsSearchComplete])

    React.useEffect(() => {
        STATE.Step1_2Valid = requiredOptionsComplete
        dispatch(ACTIONS.SET_REQUIRED_OPTIONS_STATE(requiredOptionsComplete)) //REQUIRED OPTIONS STATE
    }, [requiredOptionsComplete])

    React.useEffect(() => {
        STATE.Step1_3Valid = addressSearchComplete !== ""
        dispatch(ACTIONS.SET_PROPERTY_SEARCH_STATE(addressSearchComplete !== "")) //PROPERTY SEARCH STATE
    }, [addressSearchComplete])

    React.useEffect(() => {
        const isExpiringIn30Days = restrictionExpiresIn30Days > -1
        STATE.Step1_4Valid = coverageSearchComplete || isExpiringIn30Days
        dispatch(ACTIONS.SET_AREA_COVERAGE_STATE(coverageSearchComplete || isExpiringIn30Days)) //AREA COVERAGE STATE
    }, [coverageSearchComplete, restrictionExpiresIn30Days])


    React.useEffect(() => {
        const isExpiringIn30Days = restrictionExpiresIn30Days > -1
        const s1c = (jobsSearchComplete && addressSearchComplete !== "" && (coverageSearchComplete || isExpiringIn30Days) && requiredOptionsComplete)
        ui.Stage1Complete = s1c
    }, [jobsSearchComplete, addressSearchComplete, coverageSearchComplete, requiredOptionsComplete, state, stage1Complete, restrictionExpiresIn30Days])

    React.useEffect(() => {
        setActiveStep(getActiveStep(steps))
    }, [activeStep, stage1Complete, steps])

    const debugName = `${parent}▷[${name}]`

    useDebugInformation(debugName, { state, stage1Complete, steps, activeStep, coverageSearchComplete, requiredOptionsComplete })

    return (
        <Box id="left-panel" sx={{ width: 260, p: 1 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                    < Step key={step.label} >
                        <StepLabel
                            optional={
                                index === steps.length - 1 ? (
                                    <Typography variant="caption">Last step of this stage</Typography>
                                ) : null
                            }
                        >
                            {step.label}
                        </StepLabel>
                        <StepContent>
                            <Typography>{step.description}</Typography>
                            {/* {index === 2 && step.description.toLowerCase().includes("fail") && <Button variant='contained' color='secondary'>FAIL No Coverage</Button>} */}
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
            {
                activeStep === steps.length && (
                    <Paper square elevation={0} sx={{ p: 3 }}>
                        <Typography>All steps completed - please continue to next stage!</Typography>
                    </Paper>
                )
            }
        </Box >
    );
}

Stage1Panel.displayName = name;


export default React.memo(Stage1Panel);